// Which pronunciation recordings a build actually has.
//
// Mynah ships its clips three ways and they are not the same set. The served app has all 18,696;
// the phone has a clip for every word with a card plus the eighty story lines, and fetches the
// 145 MB of sentence audio afterwards; a build made without `npm run audio` has none at all.
//
// THE APP HAS TO KNOW WHICH BEFORE IT PLAYS, not after. Finding out by asking for a file and
// waiting for it to fail is too late on a phone: the failure arrives asynchronously, long after
// the tap, and iOS will not start speech synthesis outside a user gesture. So the button did
// nothing, said nothing, and did nothing again the next time it was pressed.
//
// Hence `covers`. The manifest states what the build holds, the app reads it synchronously, and a
// card with no recording goes straight to the system voice while the tap is still live.

/** What a build's audio directory holds. Absent on a manifest written before this existed. */
export interface ClipCoverage {
  /**
   * 'all' — every clip that was rendered, including words that only appear inside sentences.
   * 'vocabulary' — one for each word with a card of its own, and no others.
   * false — none.
   */
  words: 'all' | 'vocabulary' | false;
  sentences: boolean;
  stories: boolean;
  /**
   * Words this build deliberately did NOT record, though `words` says otherwise.
   *
   * THE ONE CASE, and it is a limit of the synthesiser rather than of the pipeline. Piper's
   * Japanese voice cannot say a single mora: given one kana with no sentence around it the model
   * is outside anything it was trained on and the output degenerates -- あ comes out SHORTER when
   * the voice is slowed, and 「か、か、か。」 collapses into a third of a second with no pauses in
   * it. Those clips are the particles a beginner meets first, and they sounded, in Kennard's
   * words, nothing like the actual sound (MYN-174).
   *
   * So they are not shipped, and the device says them instead -- which is what the app already
   * does for any word it has no recording for, and which macOS and iOS do well.
   *
   * NAMED RATHER THAN INFERRED. A manifest that claims a clip the build does not hold is the
   * failure MYN-130 and MYN-132 were both about: the app asks for it, gets a 404, and the learner
   * hears nothing where they expected a word. Saying so here keeps the manifest matching the clips
   * in both directions.
   */
  except?: string[];
}

/**
 * One course's recordings, as a build describes them.
 *
 * Split out of ClipManifest so a manifest can describe more than one. The fields are the same
 * ones the manifest has always carried; what changed is that they are now a description of a
 * course rather than a description of the file they are written in.
 */
export interface ClipSet {
  format: string;
  voice: string;
  count: number;
  words?: number;
  sentences?: number;
  storyLines?: number;
  covers?: ClipCoverage;
  /** What this build left behind for the app to fetch, counted at build time. */
  absent?: { clips: number; bytes: number };
  /** Where the rest of the recordings live. Stated per build so a rebrand changes one line. */
  origin?: string;
  /**
   * The directory this build's clips are in, relative to the app, ending in a slash.
   *
   * A pair's recordings live under audio/<pair>/ because a word clip is named after the word it
   * speaks — 一.mp3 is one thing in a Chinese course and another in a Japanese one, and they
   * cannot share a directory. The manifest itself stays at audio/manifest.json, which is how the
   * app finds the prefix without already knowing it.
   *
   * Absent on a manifest written before this existed, where the clips sat at audio/ directly.
   */
  prefix?: string;
}

/**
 * audio/manifest.json: what recordings this deployment holds.
 *
 * THE TOP-LEVEL FIELDS ARE THE BUILD'S OWN COURSE and they are not going anywhere. An app that is
 * already installed reads them and knows nothing about `courses`, so they have to go on meaning
 * exactly what they meant before — which is why `courses` is added beside them rather than
 * replacing them. A phone build ships one course and writes only the top level.
 *
 * `courses` is for the served deployment, where MYN-107 made the course a runtime choice and
 * MYN-114 gave each one its own directory. Both sets of clips are on the same server; without a
 * manifest that can name them both, switching to Japanese found a manifest describing Chinese,
 * correctly refused to trust it (MYN-116), and fell back to the system voice with 9,526
 * recordings sitting one directory away.
 */
export interface ClipManifest extends ClipSet {
  /**
   * WHICH COURSE the top-level fields are for, as shared/item-key.ts spells a pair: "zh-CN/en".
   *
   * Absent on a manifest written before this existed, where the only pair was the bundled one.
   */
  pair?: string;
  /** Every course whose recordings are on this server, keyed by pair id. */
  courses?: Record<string, ClipSet>;
}

/** Everything, which is what a manifest without a `covers` field meant when it was written. */
export const COVERS_EVERYTHING: ClipCoverage = { words: 'all', sentences: true, stories: true };

/**
 * Is there a recording in this build for `clip`?
 *
 * `clip` is what speak() files a recording under: the characters themselves for a word,
 * `s/<tatoeba id>` for a sentence, `st/<n>` for a story line.
 *
 * @param isCardWord answers whether a word has a card of its own — asked only when the build
 *   carries the vocabulary's clips rather than all of them. The app already holds the vocabulary,
 *   so this is a set lookup rather than anything the manifest has to list.
 */
export function coversClip(
  manifest: ClipSet | null,
  clip: string,
  isCardWord: (word: string) => boolean,
): boolean {
  if (!manifest) return false;
  const covers = manifest.covers ?? COVERS_EVERYTHING;
  if (clip.startsWith('s/')) return covers.sentences;
  if (clip.startsWith('st/')) return covers.stories;
  if (covers.except?.includes(clip)) return false;
  if (covers.words === 'all') return true;
  if (covers.words === false) return false;
  return isCardWord(clip);
}

/**
 * The recordings for `pair`, or null if this deployment has none for it.
 *
 * ONE RULE, because getting it wrong is silent in both directions: answer with another course's
 * clips and every card claims a recording it does not have; answer null when the clips are there
 * and the app falls back to a system voice it did not need.
 *
 * A named course wins, because it is a statement about that course specifically. Otherwise the
 * top level answers only for its own pair — and a manifest with no `pair` at all predates the
 * field, from when a build had exactly one course, so it answers for whatever is asked.
 */
export function clipsFor(manifest: ClipManifest | null, pair: string): ClipSet | null {
  if (!manifest) return null;
  const named = manifest.courses?.[pair];
  if (named) return named;
  return !manifest.pair || manifest.pair === pair ? manifest : null;
}
